import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useExportTransactionsCSV } from '@/hooks/api/useBilling';
import { ErrorMessage, LoadingSpinner } from '@/components/ui';

const exportSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  transaction_type: z.string().optional(),
  status: z.string().optional(),
});

type ExportFormData = z.infer<typeof exportSchema>;

interface BillingExportProps {
  className?: string;
}

export const BillingExport: React.FC<BillingExportProps> = ({ className = '' }) => {
  const { mutateAsync: exportCSV, isPending: isExporting } = useExportTransactionsCSV();
  const [successMessage, setSuccessMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ExportFormData>({
    resolver: zodResolver(exportSchema),
  });

  const onSubmit = async (data: ExportFormData) => {
    try {
      // Filter out empty values
      const params = Object.entries(data).reduce(
        (acc, [key, value]) => {
          if (value && value !== '') {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>
      );

      await exportCSV(params);
      setSuccessMessage('Export downloaded successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Export failed. Please try again.';
      setError('root', {
        type: 'manual',
        message: errorMessage,
      });
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Export Transaction History</h2>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}

      {errors.root && (
        <ErrorMessage message={errors.root.message || 'An error occurred'} className="mb-4" />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              {...register('start_date')}
              type="date"
              id="start_date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              {...register('end_date')}
              type="date"
              id="end_date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="transaction_type"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Transaction Type
            </label>
            <select
              {...register('transaction_type')}
              id="transaction_type"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Types</option>
              <option value="search">Search</option>
              <option value="calculation">Calculation</option>
              <option value="payment">Payment</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              {...register('status')}
              id="status"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={isExporting}
          className="
            w-full px-6 py-3 bg-indigo-600 text-white rounded-md
            hover:bg-indigo-700 transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed
            flex items-center justify-center gap-2
            font-medium
          "
        >
          {isExporting ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span>Download CSV</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Export Information</h3>
        <ul className="text-xs text-gray-700 space-y-1">
          <li>Export includes all transaction details in CSV format</li>
          <li>Leave filters empty to export all transactions</li>
          <li>Date range helps filter specific periods</li>
          <li>Perfect for accounting and record-keeping</li>
        </ul>
      </div>
    </div>
  );
};
